import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Container, Grid } from "@mui/material";
import { Button } from "primereact/button";
import Breadcrumb from "../../../../common/Breadcrumb";
import { useStyles } from "../../Affiliate/style";
import { useNavigate } from "react-router-dom";
import { APP_URLS } from "../../../../utils/constant";
import { EzToast, TOAST_SEVERITY } from "../../../../common/EzToast";
import EzControlledInputWithFloatingLabel from "../../../../common/EzForm/EzControlledInput/WithFloatingLabel";
import EzControlledDropDowntWithFloatingLabel from "../../../../common/EzForm/EzControlledDropDown/WithFloatingLabel";
import EzControlledCalendarWithFloatingLabel from "../../../../common/EzForm/EzControlledCalendar/WithFloatingLabel";
import EzControlledCheckBoxWithLabel from "../../../../common/EzForm/EzControlledCheckBox/WithLabel";
import { basicDetail, editMode, statusDD } from "./constant";
import InvoicePayments from "../InvoicePayments";

const CreateAffiliateInvoiceComponent = (props) => {
  const {
    createAffiliateInvoice,
    editAffiliateInvoice,
    isLoading,
    isCreateSuccess,
    isCreateError,
    isEditSuccess,
    isEditError,
    isDetailSuccess,
    isEdit,
    isView,
    formFieldValueMap,
    invoiceId,
    invoicePayments,
    fetchAffiliateInvoicePayments,
    recordAffiliatePayment,
    updateAffiliatePayment,
    isRecordPaymentSuccess,
    isRecordPaymentError,
    isUpdatePaymentSuccess,
    isUpdatePaymentError,
  } = props;

  const navigate = useNavigate();
  const classes = useStyles();
  const [showSentWarning, setShowSentWarning] = useState(false);
  const isSentStatus = isEdit && formFieldValueMap?.invoiceStatus === "sent";

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    watch,
    trigger,
    setError,
    clearErrors,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(() => formFieldValueMap, [formFieldValueMap]),
  });

  const breadcrumbD = [
    { path: "/dashboard", label: "Dashboard" },
    { path: "/affiliate", label: "Affiliate" },
    { path: APP_URLS.AFFILIATE_INVOICE.HOME, label: "Affiliate Invoices" },
    {
      path: "#",
      label: isView
        ? `Invoice #${invoiceId || ""}`
        : isEdit
        ? "Edit Invoice"
        : "Create Invoice",
    },
  ];

  useEffect(() => {
    if (isCreateSuccess) {
      reset();
    }
  }, [isCreateSuccess, reset]);

  useEffect(() => {
    if (isDetailSuccess && formFieldValueMap) {
      reset({ ...formFieldValueMap });
    }
  }, [isDetailSuccess, formFieldValueMap, reset]);

  useEffect(() => {
    if (showSentWarning) {
      const t = setTimeout(() => setShowSentWarning(false), 3500);
      return () => clearTimeout(t);
    }
  }, [showSentWarning]);

  const getFormErrorMessage = (name) =>
    errors[name] ? (
      <small className="p-error">{errors[name].message}</small>
    ) : null;

  const getOptions = (name) => {
    switch (name) {
      case "invoiceStatus":
        return statusDD;
      default:
        return [];
    }
  };

  const getValidationRule = (rule, fieldName) => {
    if (isView) return {};
    
    if (fieldName === "totalCommissionAmount") {
      return {
        ...rule,
        validate: (value) => {
          if (!value) return true;

          const cleanValue = String(value).replace(/[$,]/g, '');
          const totalCommission = parseFloat(cleanValue);

          const confirmedAmountRaw = watch("confirmedReservationAmount") || "0";
          const cleanConfirmedAmount = String(confirmedAmountRaw).replace(/[$,]/g, '');
          const confirmedAmount = parseFloat(cleanConfirmedAmount);
          
          if (isNaN(totalCommission)) return "Invalid amount";
          if (totalCommission < 0) return "Amount cannot be negative";
          
          if (totalCommission > confirmedAmount) {
            return "Total Commission Amount cannot be greater than the Confirm Amount";
          }
          
          return true;
        },
      };
    }
    
    return rule || {};
  };

  const isDraftStatus = isEdit && formFieldValueMap?.invoiceStatus === "draft";
  const draftOnlyFields = ["dueDate", "totalCommissionAmount"];

  const renderField = (field) => {
    const isDraftOnlyField = draftOnlyFields.includes(field.name);
    const disabled =
      isView ||
      isSentStatus ||
      (isEdit && field.name !== "invoiceStatus" && !(isDraftOnlyField && isDraftStatus)) ||
      (!isEdit && !!field.disabled);
    const rules = getValidationRule(field.rule, field.name);

    switch (field.type) {
      case "dropdown":
        return (
          <EzControlledDropDowntWithFloatingLabel
            name={field.name}
            control={control}
            rules={rules}
            optionLabel="name"
            optionValue="id"
            options={getOptions(field.name)}
            isError={!!errors[field.name]}
            errorMsg={getFormErrorMessage(field.name)}
            label={field.label}
            labelClassName="ml-2"
            disabled={disabled}
          />
        );
      case "date":
        return (
          <EzControlledCalendarWithFloatingLabel
            name={field.name}
            control={control}
            rules={rules}
            label={field.label}
            isError={!!errors[field.name]}
            errorMsg={getFormErrorMessage(field.name)}
            maxDate={null}
            disabled={disabled}
          />
        );
      case "hours":
        return (
          <EzControlledCalendarWithFloatingLabel
            name={field.name}
            control={control}
            rules={rules}
            label={field.label}
            isError={!!errors[field.name]}
            errorMsg={getFormErrorMessage(field.name)}
            timeOnly
            showTime
            maxDate={null}
            disabled={disabled}
          />
        );
      case "checkbox":
        return (
          <EzControlledCheckBoxWithLabel
            name={field.name}
            control={control}
            rules={rules}
            label={field.label}
            isError={!!errors[field.name]}
            errorMsg={getFormErrorMessage(field.name)}
            disabled={disabled}
          />
        );
      case "number":
        return (
          <EzControlledInputWithFloatingLabel
            name={field.name}
            control={control}
            rules={rules}
            label={field.label}
            type="number"
            isError={!!errors[field.name]}
            errorMsg={getFormErrorMessage(field.name)}
            disabled={disabled}
          />
        );
      default:
        return (
          <EzControlledInputWithFloatingLabel
            name={field.name}
            control={control}
            rules={rules}
            label={field.label}
            type="text"
            isError={!!errors[field.name]}
            errorMsg={getFormErrorMessage(field.name)}
            disabled={disabled}
          />
        );
    }
  };

  const onSubmit = (data) => {
    if (!isEdit && !isDirty) return;

    const cleanTotalCommission = String(data.totalCommissionAmount || "0").replace(/[$,]/g, '');
    const totalCommission = parseFloat(cleanTotalCommission);
    
    const cleanConfirmedAmount = String(data.confirmedReservationAmount || "0").replace(/[$,]/g, '');
    const confirmedAmount = parseFloat(cleanConfirmedAmount);

    if (!isNaN(totalCommission) && totalCommission > confirmedAmount) {
      setError("totalCommissionAmount", {
        type: "manual",
        message: "Total Commission Amount cannot be greater than the Confirm Amount",
      });
      return;
    }

    const payload = {
      ...data,
      userId: localStorage.getItem("userId"),
    };

    if (isEdit) {
      editAffiliateInvoice({ id: invoiceId, data: payload });
    } else {
      createAffiliateInvoice(payload);
    }
  };

  const getToastProps = () => {
    if (isCreateSuccess || isEditSuccess) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: isEdit
          ? "Invoice Updated Successfully"
          : "Invoice Created Successfully",
        shouldShowToast: true,
      };
    }
    if (isCreateError || isEditError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: isEdit
          ? "Error while updating Invoice"
          : "Error while creating Invoice",
        shouldShowToast: true,
      };
    }
    if (isRecordPaymentSuccess) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Payment Added Successfully",
        shouldShowToast: true,
      };
    }
    if (isRecordPaymentError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: "Error while adding Payment",
        shouldShowToast: true,
      };
    }
    if (isUpdatePaymentSuccess) {
      return {
        severity: TOAST_SEVERITY.SUCCESS,
        toastTitle: "Payment Updated Successfully",
        shouldShowToast: true,
      };
    }
    if (isUpdatePaymentError) {
      return {
        severity: TOAST_SEVERITY.ERROR,
        toastTitle: "Error while updating Payment",
        shouldShowToast: true,
      };
    }
    if (showSentWarning) {
      return {
        severity: TOAST_SEVERITY.WARN,
        toastTitle: "Cannot update an invoice with 'Sent' status.",
        shouldShowToast: true,
      };
    }
    return { shouldShowToast: false };
  };

  return (
    <>
    <Container maxWidth="lg">
      <Breadcrumb list={breadcrumbD} />
      <EzToast {...getToastProps()} />

      <div className={`containerbox ${classes.bg}`}>
        <div className="make-relative sub-header">
          <div className="maintitlediv">
            <div className="ml-3">
              <h4 className="pagetitle">
                {isView
                  ? "View Affiliate Invoice"
                  : isEdit
                  ? "Edit Affiliate Invoice"
                  : "Create Affiliate Invoice"}
              </h4>
            </div>
            <div className="buttonarea">
              <Button
                tooltip="Back to Invoices"
                tooltipOptions={{ position: "left" }}
                label="Back to Invoices"
                className="p-button"
                style={{ borderRadius: "10px" }}
                onClick={() => navigate(APP_URLS.AFFILIATE_INVOICE.HOME)}
              />
            </div>
          </div>
        </div>

        <div className="form-demo">
          <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            <Grid container spacing={2} style={{ marginTop: "20px" }}>
              {basicDetail.map((field) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={field.name}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>

            {!isView && (
              <div className="make-flex mt-3 mr-2">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "end",
                    alignItems: "center",
                  }}
                >
                  <div style={{ margin: "15px 0px 15px 10px", minWidth: "100px" }}>
                    <Button
                      type={isSentStatus ? "button" : "submit"}
                      label={isEdit ? "Update" : "Save"}
                      className="p-button-raised"
                      loading={isLoading}
                      disabled={isLoading || (!isEdit && !isDirty)}
                      onClick={isSentStatus ? () => setShowSentWarning(true) : undefined}
                    />
                  </div>
                </div>
              </div>
            )}

            {(isEdit || isView) && (
              <Grid className="modsection" container spacing={2}>
                {editMode.map((field) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={field.name}>
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            )}
          </form>
        </div>
      </div>
    </Container>

      {isView && invoiceId && (
        <Container maxWidth="lg" style={{ marginTop: "20px" }}>
          <InvoicePayments
            paymentData={{
              columns: invoicePayments?.[invoiceId]?.columns || [],
              rows: invoicePayments?.[invoiceId]?.rows || [],
            }}
            paginationInfo={invoicePayments?.[invoiceId]?.paginationInfo || null}
            isLoading={invoicePayments?.[invoiceId]?.isLoading || false}
            invoiceId={invoiceId}
            onAddPayment={(payload) => recordAffiliatePayment(payload, invoiceId)}
            onUpdatePayment={updateAffiliatePayment}
            onPageChange={(page, size) => fetchAffiliateInvoicePayments({ invoiceId, page, size })}
            isSubmittingPayment={isLoading}
            isUpdatingPayment={isLoading}
            isUpdatePaymentSuccess={isUpdatePaymentSuccess}
            isUpdatePaymentError={isUpdatePaymentError}
            isPageLevelError={false}
            invoiceSummary={invoicePayments?.[invoiceId]?.summary || {
              totalCommissionAmount: 0,
              paidAmt: 0,
              unpaidAmt: 0,
            }}
          />
        </Container>
      )}
    </>
  );
};

export default CreateAffiliateInvoiceComponent;
